import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getArticleBySlug } from '../utils/api';
import SmartImage from '../components/SmartImage';
import { Button } from '../components/ui/button';
import { Calendar, User, ArrowLeft, BookOpen, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('es-ES', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return iso; }
};

const ArticleDetail = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getArticleBySlug(slug)
      .then(r => setArticle(r.data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;

  if (!article) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-muted-foreground">Artículo no encontrado</p>
      <Link to="/articulos"><Button variant="outline">Volver a artículos</Button></Link>
    </div>
  );

  const url = window.location.href;
  const text = encodeURIComponent(article.title);
  const enc = encodeURIComponent(url);
  const featuredCover = article.featured_image
    ? (article.featured_image.startsWith('/api') ? `${API_BASE}${article.featured_image}` : article.featured_image)
    : null;
  const ogImage = featuredCover || `${window.location.origin}/iaasp_logo.png`;

  const seoTitle = article.seo_title || `${article.title} — IAASP Academy`;
  const seoDesc = article.seo_description || article.excerpt || article.title;

  const copyLink = () => {
    navigator.clipboard.writeText(url).then(() => toast.success('Enlace copiado'));
  };

  return (
    <article className="min-h-screen bg-white" data-testid="article-detail-page">
      <Helmet
        title={seoTitle}
        meta={[
          { name: 'description', content: seoDesc },
          { property: 'og:title', content: seoTitle },
          { property: 'og:description', content: seoDesc },
          { property: 'og:type', content: 'article' },
          { property: 'og:url', content: url },
          { property: 'og:image', content: ogImage },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: seoTitle },
          { name: 'twitter:description', content: seoDesc },
          { name: 'twitter:image', content: ogImage },
        ]}
      />

      {featuredCover && (
        <div className="w-full bg-gradient-to-b from-secondary to-white">
          <div className="max-w-4xl mx-auto">
            <SmartImage src={featuredCover} alt={article.title} fallbackLabel={article.title} className="w-full max-h-[480px] object-cover" />
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <Link to="/articulos" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6">
          <ArrowLeft className="w-4 h-4" /> Volver a artículos
        </Link>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-primary mb-4 leading-tight">
          {article.title}
        </h1>

        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8 flex-wrap">
          {article.author_name && (
            <span className="inline-flex items-center gap-1"><User className="w-4 h-4" />{article.author_name}</span>
          )}
          <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />{fmtDate(article.created_at)}</span>
        </div>

        {article.excerpt && (
          <p className="text-lg text-muted-foreground italic border-l-4 border-accent pl-4 mb-8">
            {article.excerpt}
          </p>
        )}

        <div
          className="lesson-content prose-article"
          style={{ lineHeight: 1.7 }}
          dangerouslySetInnerHTML={{ __html: article.body || '' }}
        />

        {article.course_id && article.course_title && (
          <div className="mt-10 p-6 bg-gradient-to-br from-primary to-[#1a2744] rounded-2xl text-white" data-testid="related-course-cta">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                <BookOpen className="w-6 h-6 text-accent" />
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wide text-accent mb-1">Curso relacionado</p>
                <h3 className="text-xl font-heading font-bold mb-2">{article.course_title}</h3>
                <Link to={`/courses/${article.course_id}`}>
                  <Button className="bg-accent hover:bg-accent/90 text-[#0b1220] font-semibold" data-testid="related-course-btn">
                    Ver curso
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="mt-12 pt-6 border-t flex items-center justify-between flex-wrap gap-4">
          <span className="text-sm font-semibold text-muted-foreground inline-flex items-center gap-2">
            <Share2 className="w-4 h-4" /> Compartir:
          </span>
          <div className="flex items-center gap-2">
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc}`} target="_blank" rel="noreferrer"
              className="p-2 rounded-full hover:bg-blue-50 transition-colors" title="Facebook" data-testid="art-share-fb">
              <Facebook className="w-5 h-5 text-blue-600" />
            </a>
            <a href={`https://twitter.com/intent/tweet?url=${enc}&text=${text}`} target="_blank" rel="noreferrer"
              className="p-2 rounded-full hover:bg-sky-50 transition-colors" title="Twitter" data-testid="art-share-tw">
              <Twitter className="w-5 h-5 text-sky-500" />
            </a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc}`} target="_blank" rel="noreferrer"
              className="p-2 rounded-full hover:bg-blue-50 transition-colors" title="LinkedIn" data-testid="art-share-ln">
              <Linkedin className="w-5 h-5 text-blue-700" />
            </a>
            <a href={`https://wa.me/?text=${text}%20${enc}`} target="_blank" rel="noreferrer"
              className="px-3 py-1.5 rounded-full bg-green-50 hover:bg-green-100 text-green-700 text-xs font-semibold transition-colors" data-testid="art-share-wa">
              WhatsApp
            </a>
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="art-copy-link">
              Copiar enlace
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default ArticleDetail;
